import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Show,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { Severity, captureException } from '@sentry/nextjs';
import { useAPYQuery } from '@stader-labs/web-sdk';
import { EventParams } from 'firebase/analytics';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { mainnet } from 'viem/chains';

import Item from '@/components/Defi';
import ChainDropdown from '@/components/Defi/ChainDropdown';
import DefiTable from '@/components/Defi/DefiTable';
import { DefiTopOpportunity } from '@/components/Defi/DefiTopOpportunity';
import { NormalizedDefiItem } from '@/components/Defi/defi-page.types';
import { DefiDataProps, DefiModal } from '@/components/DefiModal';
import Icon from '@/components/Icon';
import { DEFI_CAROUSEL_CTA } from '@/constants/analytics';
import { BNB } from '@/constants/constants';
import {
  SENTRY_TAGS,
  SENTRY_TAGS_TYPE_VALUES,
} from '@/constants/monitoring.constants';
import {
  DefiData,
  DefiItemActionData,
  StrapiDefiPageData,
} from '@/customtypes/global';
import { trackEvent } from '@/dapp-sdk-v2';
import { iconMapping } from '@/icons';
import { useDefiPageCMSData } from '@/providers/CMSDataProvider';
import { getDefiData } from '@/services/defi';
import { TextPrimary, TextSecondary } from '@/themes';
import { DefiPageData } from '@/types/cms/defi-page.types';
import { getCurrentChain } from '@/utils/common';
import { formatNumber } from '@/utils/globalCommon';

import styles from './styles.module.scss';

const Disclaimer = dynamic(() => import('@/components/Disclaimer'), {
  ssr: false,
});

export type TChainOption = {
  id: string;
  label: string;
  icon?: string;
  checked?: boolean;
};

const ALL_FILTER = {
  label: 'All',
  id: 'all',
};

const DeFiPage = () => {
  const defiStrapiPageData = useDefiPageCMSData((data) => data.defi);

  const router = useRouter();
  const { isReady, pathname, query } = router;
  const token = getCurrentChain(pathname);

  const { defiChain: chainInUrl, activity: filtersInUrl } = query;
  const { isOpen, onClose } = useDisclosure();
  const [
    defiModalData,
    // setDefiModaldata
  ] = useState<DefiDataProps | null>(null);

  const { data: apyData } = useAPYQuery();

  const apy =
    apyData?.apy && apyData?.apy && apyData.apy > 0
      ? Number(Number(apyData.apy).toFixed(2))
      : 0;

  const [data, setData] = useState<DefiData>({});
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<Array<TChainOption>>([]);
  const [availableChains, setAvailableChains] = useState<Array<TChainOption>>(
    [],
  );

  // TODO: move to react query part:1/2
  const [defiItems, setDefiItems] = useState<Array<NormalizedDefiItem>>([]);

  const getAvailableChains = (items: any[]) => {
    const alreadyAddedChains = new Set();
    const selectedChains = (String(chainInUrl).split(',') || []).map(
      (chain: string) => chain.trim(),
    );
    const allChainsSelected = selectedChains.includes(ALL_FILTER.id);

    const filters = items.reduce(
      (acc: TChainOption[], item: NormalizedDefiItem) => {
        const chain = item.chain;
        if (chain && !alreadyAddedChains.has(chain.key)) {
          alreadyAddedChains.add(chain.key);
          return [
            ...acc,
            {
              id: chain.key,
              label: chain.title,
              checked: allChainsSelected || selectedChains.includes(chain.key),
            },
          ];
        }
        return acc;
      },
      [],
    );
    if (filters.length > 0)
      filters.unshift({
        ...ALL_FILTER,
        checked: allChainsSelected,
      });
    return filters;
  };

  const handleSelectChain = (option: TChainOption, value: any) => {
    if (option.id === ALL_FILTER.id) {
      const updatedChains = availableChains.map((filter) => ({
        ...filter,
        checked: value,
      }));
      updateFiltersInUrl('defiChain', updatedChains);
      setAvailableChains(updatedChains);
    } else {
      const updatedChains = availableChains.map((chain) =>
        chain.id === option.id ? { ...chain, checked: value } : chain,
      );
      const allChecked = updatedChains
        .slice(1)
        .every((option) => option.checked);

      updatedChains[0].checked = allChecked;
      updateFiltersInUrl('defiChain', updatedChains);
      setAvailableChains(updatedChains);
    }
  };

  const logFirebaseEvent = useCallback(
    (event: string, eventParams: EventParams = {}) => {
      trackEvent(event, {
        ...eventParams,
      });
    },
    [],
  );

  const defiPageData: StrapiDefiPageData = useMemo(() => {
    return {
      ...defiStrapiPageData,
      banner: defiStrapiPageData?.banner,
      // defiItems: getDefiItemsData(defiStrapiPageData?.defi_items),
      title: defiStrapiPageData?.page_title,
      subTitle: defiStrapiPageData?.page_subtitle,
      tooltipText: defiStrapiPageData?.tooltip_text,
    };
  }, [defiStrapiPageData]);

  const getAPY = useCallback(
    (item: any) => {
      let APY: string | number = data[item.apiKey]?.apy;
      if (item?.should_add_staking_apy) {
        APY = Number(APY) + apy;
      }
      return APY ? formatNumber(Number(APY), false, 2) : APY;
    },
    [apy, data],
  );

  const { banner, tooltipText } = defiPageData;

  const getDefiItems = (defiData: DefiPageData) => {
    return (defiData.defi_items || []).reduce(
      (res: NormalizedDefiItem[], section) => {
        const currentItems: NormalizedDefiItem[] = section.items.map(
          (item) =>
            ({
              apiKey: item?.api_key,
              apyMessage: item?.apy_message,
              showToken: item?.show_token,
              token: item?.token,
              platform: item?.platform_detail?.data?.attributes ?? null,
              pairPlatform:
                item?.pair_platform_detail?.data?.attributes ?? null,
              type: item?.type?.data?.attributes?.key || '',
              tokenIconKey: item?.token_icon_key,
              poolKey: item?.pool_id,
              should_add_staking_apy: item?.should_add_staking_apy,
              sectionId: String(section.id),
              sectionTitle: section.title,
              isSDPool: section.is_sd_pools,
              chain: item.de_fi_network_type.data?.attributes ?? null,
              links:
                item?.action_links?.data?.attributes?.links?.map((link) => {
                  return {
                    actionCta: link?.action_cta,
                    actionLink: link?.action_link,
                    buttonType: link?.button_type,
                  } satisfies DefiItemActionData;
                }) ?? [],
              tags: item.tags,
            }) satisfies NormalizedDefiItem,
        );

        return [...res, ...currentItems];
      },
      [],
    );
  };

  const getAvailableFilters = (items: any) => {
    const alreadyAddedFilters = new Set();
    const selectedActivities = (String(filtersInUrl).split(',') || []).map(
      (activity: string) => activity.trim(),
    );
    const allActivitiesSelected = selectedActivities.includes(ALL_FILTER.id);

    const filters = items.reduce(
      (acc: TChainOption[], item: NormalizedDefiItem) => {
        if (!alreadyAddedFilters.has(item.sectionId)) {
          alreadyAddedFilters.add(item.sectionId);
          const sectionId = String(item.sectionId);
          return [
            ...acc,
            {
              id: sectionId,
              label: item.sectionTitle,
              checked:
                allActivitiesSelected || selectedActivities.includes(sectionId),
            },
          ];
        }
        return acc;
      },
      [],
    );
    if (filters.length > 0)
      filters.unshift({
        ...ALL_FILTER,
        checked: allActivitiesSelected,
      });
    return filters;
  };

  useEffect(() => {
    if (!isReady) return;
    const selectedActivities = (String(filtersInUrl).split(',') || [])
      .map((activity: string) => activity.trim())
      .filter((activity: string) => activity !== ALL_FILTER.id);
    const invalidActivities = selectedActivities.reduce(
      (acc: boolean, activity: string) => isNaN(Number(activity)) || acc,
      false,
    );

    const selectedChains = availableChains.map((chain) => chain.id);

    const invalidChain = (String(chainInUrl).split(',') || [])
      .map((chain: string) => chain.trim())
      .filter((chain: string) => chain !== ALL_FILTER.id && chain.length > 0)
      .reduce(
        (acc: boolean, chain: string) =>
          (selectedChains.length > 0 && !selectedChains.includes(chain)) || acc,
        false,
      );

    if (invalidChain || invalidActivities) {
      router.replace(
        {
          query: {
            ...router.query,
            defiChain: ALL_FILTER.id,
            activity: ALL_FILTER.id,
          },
        },
        undefined,
        { shallow: true },
      );
    }
  }, [chainInUrl, filtersInUrl, availableChains]);

  // TODO: move to react query part:2/2
  useEffect(() => {
    (async function () {
      try {
        setLoading(true);
        let items = getDefiItems(defiStrapiPageData) || [];
        const defiData = await getDefiData(items, token);
        items = items.filter((item: any) =>
          token === BNB
            ? defiData[item.apiKey]?.enable
            : defiData[item.apiKey] || false,
        );

        setDefiItems(items);
        setData(defiData);
      } catch (err) {
        captureException(err, {
          level: Severity.Error,
          tags: {
            [SENTRY_TAGS.TYPE]: SENTRY_TAGS_TYPE_VALUES.CMS,
            [SENTRY_TAGS.OPERATION]: 'getDefiData',
          },
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [defiStrapiPageData]);

  useEffect(() => {
    if (filtersInUrl || activities.length === 0) {
      const updatedActivities = getAvailableFilters(defiItems);
      setActivities(updatedActivities);
    }
    if (chainInUrl || availableChains.length === 0) {
      const chains = getAvailableChains(defiItems);
      setAvailableChains(chains);
    }
  }, [defiItems, filtersInUrl]);

  const updateFiltersInUrl = (key: string, filters: TChainOption[]) => {
    const selectedOptions = filters
      .filter((option) => option.checked)
      .map((option) => option.id)
      .join(',');

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          [key]: filters[0]?.checked ? ALL_FILTER.id : selectedOptions,
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const handleSelectActivity = (option: TChainOption, value: any) => {
    if (option.id === ALL_FILTER.id) {
      const updatedActivities = activities.map((filter) => ({
        ...filter,
        checked: value,
      }));
      updateFiltersInUrl('activity', updatedActivities);
      setActivities(updatedActivities);
    } else {
      const updatedActivities = activities.map((filter) =>
        filter.id === option.id ? { ...filter, checked: value } : filter,
      );
      const allChecked = updatedActivities
        .slice(1)
        .every((option) => option.checked);

      updatedActivities[0].checked = allChecked;
      updateFiltersInUrl('activity', updatedActivities);
      setActivities(updatedActivities);
    }
  };

  const selectedActivities = activities
    .filter((activity) => activity.checked)
    .map((activity) => activity.id);

  const selectedChains = availableChains
    .filter((chain) => chain.checked)
    .map((chain) => chain.id);

  const filteredDefiSections = useMemo(() => {
    const filteredItems = defiItems.filter(
      (item) =>
        availableChains.length === 0 ||
        selectedChains.includes(item.chain?.key as string),
    );

    const filteredItemsByChains = filteredItems.reduce(
      (acc: Record<string, NormalizedDefiItem[]>, item) => {
        const { title = '' } = item.chain || {};
        if (!acc[title]) {
          acc[title] = [];
        }
        if (selectedActivities.includes(item.sectionId as string)) {
          acc[title].push(item);
        }
        return acc;
      },
      {},
    );

    return Object.entries(filteredItemsByChains)
      .map(([key, items]) => ({
        key,
        items,
      }))
      .sort((a, b) => {
        // ? mainnet should always be on top
        if (
          a.key.localeCompare(mainnet.name, undefined, {
            sensitivity: 'base',
          }) === 0
        ) {
          return -1;
        }

        if (
          b.key.localeCompare(mainnet.name, undefined, {
            sensitivity: 'base',
          }) === 0
        ) {
          return 1;
        }

        return 0;
      });
  }, [availableChains.length, defiItems, selectedActivities, selectedChains]);

  return (
    <>
      <Box
        ml={{ base: 0, xl: 60 }}
        px={{ base: '1rem', xl: '2.5rem' }}
        mt={{ base: '1rem', lg: '1rem' }}
        height={{ xl: 'calc(100vh - 128px)' }}
        overflow={{ xl: 'auto' }}
        className={styles.hideScroll}
      >
        {banner && banner?.title ? (
          <Show above="lg">
            <Center mt="1rem">
              <Flex
                align="center"
                w="872px"
                h="160px"
                borderRadius="24px"
                gap="1rem"
                bg="linear-gradient(90deg, rgba(55,41,0) 20%, rgba(30,23,3) 50%, rgba(55,41,0) 90%);"
              >
                <Flex
                  gap={{ base: '0.5rem', xl: '1rem' }}
                  flex={1}
                  flexDir="column"
                  px="92px"
                  align="flex-start"
                >
                  <Text
                    fontSize={24}
                    fontWeight="500"
                    lineHeight="28px"
                    color="white"
                  >
                    {banner?.title}
                  </Text>
                  {banner?.cta_text && (
                    <Button
                      as="a"
                      href={banner?.cta_link}
                      target="_blank"
                      rel="noreferrer"
                      fontWeight="500"
                      fontSize={16}
                      bg="primary"
                      onClick={() => {
                        logFirebaseEvent(DEFI_CAROUSEL_CTA);
                      }}
                    >
                      {banner.cta_text}
                    </Button>
                  )}
                </Flex>
                <Box mr="72px">
                  {banner.img_key && (
                    <Icon
                      height="160px"
                      width="160px"
                      Icon={iconMapping(banner.img_key)}
                    />
                  )}
                </Box>
              </Flex>
            </Center>
          </Show>
        ) : null}
        {
          <Flex
            flexDir="column"
            gap="0.5rem"
            align={{ base: 'flex-start', lg: 'center' }}
            mt={banner ? '2.5rem' : 0}
          >
            <Flex align="center" gap="1.5rem">
              <TextPrimary
                fontSize={{ base: '20px', lg: '28px' }}
                lineHeight={{ base: '24px', lg: '30px' }}
                fontWeight="600"
              >
                {defiPageData?.title}
              </TextPrimary>
              {tooltipText && (
                <Tooltip
                  label={
                    <Box pointerEvents="all" textAlign="left" maxW="250px">
                      {tooltipText}
                    </Box>
                  }
                  p="1rem"
                  borderRadius="8px"
                  closeDelay={2000}
                >
                  <Text ml="4px" color="textSecondary" fontSize="14px">
                    <AiOutlineInfoCircle />
                  </Text>
                </Tooltip>
              )}
            </Flex>

            {defiPageData?.subTitle && (
              <TextSecondary
                fontSize={{ base: '12px', lg: '16px' }}
                textAlign={{ base: 'left', lg: 'center' }}
              >
                {defiPageData?.subTitle}
              </TextSecondary>
            )}

            <Flex align="flex-start" gap="0.5rem" mt="1rem" width="100%">
              {availableChains.length > 0 && (
                <ChainDropdown
                  options={availableChains}
                  onSelect={handleSelectChain}
                  placeholder="Chains"
                  isCheckbox={true}
                />
              )}
              {activities.length > 0 && (
                <ChainDropdown
                  options={activities}
                  onSelect={handleSelectActivity}
                  placeholder="Activity"
                  isCheckbox={true}
                />
              )}
            </Flex>
          </Flex>
        }

        {!loading && (
          <DefiTopOpportunity
            items={defiItems}
            apyAndTvlData={data}
            stakingAPY={apy}
          />
        )}

        {!loading && filteredDefiSections.length > 0 && (
          <Flex mt={{ base: 12, lg: 4 }} mb={12} gap="8" direction="column">
            <Show above="lg">
              {filteredDefiSections.map(({ key, items }) => (
                <Flex direction="column" key={key}>
                  <TextSecondary fontWeight={600} ml={1}>
                    {key}
                  </TextSecondary>
                  {items.length > 0 && (
                    <DefiTable
                      group={''}
                      data={items}
                      hasAPY={true}
                      defiData={data}
                      stakingAPY={apy}
                    />
                  )}
                  {items.length === 0 && (
                    <Text
                      bg="backgroundSecondary"
                      padding="1.5rem"
                      borderRadius="1.5rem"
                      mt="4"
                    >
                      No assets for selected activity
                    </Text>
                  )}
                </Flex>
              ))}
            </Show>
            <Show below="lg">
              {filteredDefiSections.map(({ key, items }) => (
                <Flex direction="column" key={key}>
                  <TextSecondary fontWeight={600} ml={1}>
                    {key}
                  </TextSecondary>
                  <Flex
                    gap="1.25rem"
                    mt="4"
                    flexDir="column"
                    bg="backgroundSecondary"
                    p={{ base: '1rem 1.5rem', lg: '2rem 2.5rem' }}
                    borderRadius="1.5rem"
                  >
                    {items
                      .sort((a, b) => {
                        const a_apy = getAPY(a) || -1;
                        const b_apy = getAPY(b) || -1;

                        return Number(b_apy) - Number(a_apy);
                      })
                      .map((item, index, items) => {
                        const APY = getAPY(item);
                        return (
                          <React.Fragment key={item.apiKey}>
                            <Item
                              TVL={data[item.apiKey]?.tvl}
                              APY={APY}
                              hasAPY={true}
                              isSDPool={item.isSDPool ?? false}
                              paused={data[item.apiKey]?.paused}
                              tags={data[item.apiKey]?.tags || []}
                              itemData={data[item.apiKey]}
                              defiItem={item}
                              links={item.links}
                            />
                            {index !== items.length - 1 && (
                              <Divider borderColor="textSecondary" p="0" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    {items.length === 0 && (
                      <Text
                        bg="backgroundSecondary"
                        padding="0.5rem"
                        borderRadius="1rem"
                      >
                        No assets for selected activity
                      </Text>
                    )}
                  </Flex>
                </Flex>
              ))}
            </Show>
          </Flex>
        )}

        {loading && (
          <Flex alignItems="center" justifyContent="center" h="500px">
            <Spinner size="lg" color="primary" />
          </Flex>
        )}

        {Object.keys(filteredDefiSections).length > 0 && (
          <Box mb={20} textAlign="center">
            <Disclaimer />
          </Box>
        )}
        {!loading && Object.keys(filteredDefiSections).length === 0 && (
          <Flex justifyContent="center" align="center" height={'60vh'}>
            <TextPrimary
              fontSize={{ base: '16px', lg: '16px' }}
              fontWeight="500"
              lineHeight={{ base: '24px', lg: '28px' }}
              textAlign="center"
            >
              {selectedChains.length === 0
                ? 'Please select atleast one chain to see the defi opportunities'
                : selectedActivities.length > 0
                  ? 'No defi opportunities available for the selected activities.'
                  : 'Please select atleast one activity to see the defi opportunities'}
            </TextPrimary>
          </Flex>
        )}
      </Box>
      <DefiModal isOpen={isOpen} closeAlert={onClose} data={defiModalData} />
    </>
  );
};

export default DeFiPage;
